/**
 * Tahap 7B — Financial Control, Reports, Aging, Fixed Asset, Close & Dashboard.
 * 100% Bahasa Indonesia · konsisten dengan tema existing (slate palette).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarCard, HorizontalBarCard, LineCard, COLORS as CHART_COLORS } from "@/components/charts/DashboardCharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import {
  TrendingUp, TrendingDown, Wallet, Banknote, Building2, Calendar, ShieldCheck,
  Lock, Unlock, Package, FileText, Layers, Landmark, Truck,
} from "lucide-react";

/* -------- RBAC client-side mirror -------- */
const FIN_PERMS_BY_ROLE = {
  super_admin: ["*"],
  keuangan: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT"],
  akuntansi: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT",
              "FIN_CLOSE_KELOLA", "FIN_CLOSE_APPROVE",
              "ASET_LIHAT", "ASET_KELOLA", "ASET_DEPRECIATION", "ASET_DISPOSAL_CREATE"],
  direktur_utama: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT",
                   "FIN_CLOSE_APPROVE", "FIN_CLOSE_LOCK", "FIN_CLOSE_REOPEN",
                   "ASET_LIHAT", "ASET_DISPOSAL_APPROVE", "ASET_CATEGORY_KELOLA"],
  direktur_operasional: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "ASET_LIHAT"],
  auditor: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
  viewer: ["FIN_DASHBOARD_LIHAT", "ASET_LIHAT"],
  perpajakan: ["FIN_AGING_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
  gudang: [],
};

function useFinCan() {
  const { user } = useAuth();
  const role = user?.role;
  const perms = FIN_PERMS_BY_ROLE[role] || [];
  const isSuper = role === "super_admin" || perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}

function NoAccessBanner({ perm }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="no-access-banner">
      Tidak berwenang mengakses data ini{perm ? ` — perlu izin ${perm}` : ""}. Hubungi administrator jika Anda membutuhkan akses.
    </div>
  );
}

function Crumb({ path }) {
  return <div className="text-xs text-slate-500 mb-2">{path.join(" › ")}</div>;
}

function Stat({ label, value, tone = "slate", sub, testid }) {
  const cls = tone === "emerald" ? "text-emerald-700 bg-emerald-50" :
              tone === "rose" ? "text-rose-700 bg-rose-50" :
              tone === "amber" ? "text-amber-700 bg-amber-50" :
              "text-slate-700 bg-slate-50";
  return (
    <Card><CardContent className="p-4">
      <div className={`inline-block rounded px-2 py-0.5 text-xs mb-2 ${cls}`}>{label}</div>
      <div className="text-2xl font-bold" data-testid={testid}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </CardContent></Card>
  );
}

/* ============ Hub ============ */
export function FinancialHub() {
  const can = useFinCan();
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    if (!can("FIN_DASHBOARD_LIHAT")) return;
    api.get("/fin/dashboard").then(({ data }) => setSnap(data)).catch(() => {});
  }, [can]);
  const links = [
    { to: "/fin/cashflow", label: "Laporan Arus Kas", icon: TrendingUp, perm: "FIN_CASHFLOW_LIHAT" },
    { to: "/fin/aging-piutang", label: "Aging Piutang (AR)", icon: FileText, perm: "FIN_AGING_LIHAT" },
    { to: "/fin/aging-hutang", label: "Aging Hutang (AP)", icon: FileText, perm: "FIN_AGING_LIHAT" },
    { to: "/fin/cash-position", label: "Cash Position & Likuiditas", icon: Wallet, perm: "FIN_CASH_POSITION_LIHAT" },
    { to: "/fin/dashboard", label: "Dasbor Manajemen", icon: TrendingUp, perm: "FIN_DASHBOARD_LIHAT" },
    { to: "/fin/integritas", label: "Pusat Integritas Keuangan", icon: ShieldCheck, perm: "FIN_INTEGRITY_LIHAT" },
    { to: "/fin/tutup-buku", label: "Tutup Buku Periode", icon: Lock, perm: "FIN_INTEGRITY_LIHAT" },
    { to: "/aset/register", label: "Register Aset Tetap", icon: Building2, perm: "ASET_LIHAT" },
    { to: "/aset/penyusutan", label: "Penyusutan Batch", icon: Layers, perm: "ASET_LIHAT" },
    { to: "/aset/pelepasan", label: "Pelepasan Aset", icon: Truck, perm: "ASET_LIHAT" },
  ];
  return (
    <div className="space-y-6 page-fade" data-testid="fin-hub">
      <div>
        <Crumb path={["Keuangan & Kontrol"]} />
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Keuangan, Kontrol Kas & Aset Tetap (Tahap 7B)</h1>
        <p className="text-sm text-slate-500 mt-2">Arus kas, aging, cash position, aset tetap, penyusutan, tutup buku, dan integritas keuangan.</p>
      </div>

      {snap && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BarCard
            title="Ringkasan Laba-Rugi"
            testId="chart-fin-pnl"
            currency
            hint="Belum ada data laba-rugi untuk periode ini."
            data={[
              { name: "Revenue",      value: snap.revenue      || 0 },
              { name: "HPP",          value: snap.hpp          || 0 },
              { name: "Gross Profit", value: snap.gross_profit || 0 },
              { name: "OpEx",         value: snap.opex         || 0 },
              { name: "Net Profit",   value: snap.net_profit   || 0 },
            ]}
            bars={[{ key: "value", label: "Nominal", color: CHART_COLORS.baseline }]}
          />
          <BarCard
            title="Ringkasan Posisi Neraca"
            testId="chart-fin-balance"
            currency
            hint="Belum ada data neraca untuk periode ini."
            data={[
              { name: "Kas & Bank",   value: Math.abs(snap.cash_and_bank  || 0) },
              { name: "Piutang",      value: snap.ar                        || 0 },
              { name: "Hutang",       value: snap.ap                        || 0 },
              { name: "Persediaan",   value: snap.inventory                 || 0 },
              { name: "Aset Tetap",   value: snap.fixed_asset_cost          || 0 },
            ]}
            bars={[{ key: "value", label: "Saldo", color: CHART_COLORS.info }]}
          />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {links.filter(l => can(l.perm)).map(l => {
          const Icon = l.icon;
          return (
            <Link key={l.to} to={l.to} data-testid={`fin-link-${l.to.replace(/\//g, "-")}`}>
              <Card className="hover:shadow-md transition"><CardContent className="p-5 flex items-center gap-3">
                <Icon className="h-6 w-6 text-slate-700" />
                <div><div className="font-semibold">{l.label}</div><div className="text-xs text-slate-500">{l.to}</div></div>
              </CardContent></Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ============ Cash Flow ============ */
export function CashFlowPage() {
  const can = useFinCan();
  const canView = can("FIN_CASHFLOW_LIHAT");
  const [from, setFrom] = useState("2027-01-01");
  const [to, setTo] = useState("2027-12-31");
  const [d, setD] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/fin/cashflow?date_from=${from}&date_to=${to}&method=BOTH`); setD(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (canView) load(); }, [canView]);
  if (!canView) return <div className="space-y-6 page-fade" data-testid="cashflow-page"><Crumb path={["Keuangan", "Arus Kas"]} /><h1 className="font-display text-3xl font-bold">Laporan Arus Kas</h1><NoAccessBanner perm="FIN_CASHFLOW_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="cashflow-page">
      <div><Crumb path={["Keuangan", "Arus Kas"]} /><h1 className="font-display text-3xl font-bold">Laporan Arus Kas (Direct + Indirect)</h1></div>
      <Card><CardContent className="p-4 flex flex-wrap gap-3 items-end">
        <div><Label>Dari</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="cf-from" /></div>
        <div><Label>Sampai</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="cf-to" /></div>
        <Button onClick={load} data-testid="cf-load">Tampilkan</Button>
      </CardContent></Card>
      {d && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Kas Awal" value={formatRupiah(d.opening_cash)} testid="cf-opening" />
            <Stat label="Perubahan Bersih" value={formatRupiah(d.net_change)} tone={d.net_change >= 0 ? "emerald" : "rose"} testid="cf-net" />
            <Stat label="Kas Akhir" value={formatRupiah(d.closing_cash)} testid="cf-closing" />
          </div>
          <Tabs defaultValue="direct">
            <TabsList>
              <TabsTrigger value="direct" data-testid="cf-tab-direct">Direct Method</TabsTrigger>
              <TabsTrigger value="indirect" data-testid="cf-tab-indirect">Indirect Method</TabsTrigger>
            </TabsList>
            <TabsContent value="direct">
              <Card><CardContent className="p-4 space-y-3">
                <h3 className="font-semibold">OPERATING</h3>
                <Table><TableBody>{Object.entries(d.direct?.operating || {}).map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(v)}</TableCell></TableRow>)}<TableRow><TableCell className="font-bold">Total Operating</TableCell><TableCell className="text-right font-bold font-mono-num">{formatRupiah(d.direct?.total_operating)}</TableCell></TableRow></TableBody></Table>
                <h3 className="font-semibold">INVESTING</h3>
                <Table><TableBody>{Object.entries(d.direct?.investing || {}).map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(v)}</TableCell></TableRow>)}<TableRow><TableCell className="font-bold">Total Investing</TableCell><TableCell className="text-right font-bold font-mono-num">{formatRupiah(d.direct?.total_investing)}</TableCell></TableRow></TableBody></Table>
                <h3 className="font-semibold">FINANCING</h3>
                <Table><TableBody>{Object.entries(d.direct?.financing || {}).map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(v)}</TableCell></TableRow>)}<TableRow><TableCell className="font-bold">Total Financing</TableCell><TableCell className="text-right font-bold font-mono-num">{formatRupiah(d.direct?.total_financing)}</TableCell></TableRow></TableBody></Table>
                <div className="flex items-center gap-2 mt-4"><Badge className={d.direct?.reconciled ? "bg-emerald-600" : "bg-rose-600"} data-testid="cf-direct-recon">{d.direct?.reconciled ? "REKONSILIASI SESUAI" : "SELISIH"}</Badge><span className="text-sm text-slate-600">Selisih vs Δ kas: {formatRupiah(d.direct?.reconciliation_to_net_change)}</span></div>
              </CardContent></Card>
            </TabsContent>
            <TabsContent value="indirect">
              <Card><CardContent className="p-4 space-y-3">
                <div>Laba Bersih Periode: <span className="font-bold font-mono-num">{formatRupiah(d.indirect?.net_income)}</span></div>
                <h3 className="font-semibold">Penyesuaian Non-Kas</h3>
                <Table><TableBody>{Object.entries(d.indirect?.adjustments || {}).map(([k, v]) => <TableRow key={k}><TableCell>{k}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(v)}</TableCell></TableRow>)}</TableBody></Table>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Operating" value={formatRupiah(d.indirect?.operating_cash_flow)} />
                  <Stat label="Investing" value={formatRupiah(d.indirect?.investing_cash_flow)} />
                  <Stat label="Financing" value={formatRupiah(d.indirect?.financing_cash_flow)} />
                </div>
                <div className="flex items-center gap-2 mt-4"><Badge className={d.indirect?.reconciled ? "bg-emerald-600" : "bg-rose-600"} data-testid="cf-indirect-recon">{d.indirect?.reconciled ? "REKONSILIASI SESUAI" : "SELISIH"}</Badge><span className="text-sm text-slate-600">Selisih: {formatRupiah(d.indirect?.reconciliation_to_net_change)}</span></div>
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

/* ============ AR / AP Aging ============ */
function AgingPage({ kind }) {
  const can = useFinCan();
  const canView = can("FIN_AGING_LIHAT");
  const [d, setD] = useState(null);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const load = async () => {
    try { const url = kind === "ar" ? "/fin/ar-aging" : "/fin/ap-aging"; const { data } = await api.get(`${url}?as_of=${asOf}`); setD(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (canView) load(); }, [canView]);
  const label = kind === "ar" ? "Piutang" : "Hutang";
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Aging {label}</h1><NoAccessBanner perm="FIN_AGING_LIHAT" /></div>;
  const rows = kind === "ar" ? (d?.customers || []) : (d?.suppliers || []);
  return (
    <div className="space-y-6 page-fade" data-testid={`aging-${kind}-page`}>
      <div><Crumb path={["Keuangan", `Aging ${label}`]} /><h1 className="font-display text-3xl font-bold">Aging {label} per {kind === "ar" ? "Pelanggan" : "Pemasok"}</h1></div>
      <Card><CardContent className="p-4 flex gap-3 items-end">
        <div><Label>Per tanggal</Label><Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} data-testid={`aging-${kind}-asof`} /></div>
        <Button onClick={load} data-testid={`aging-${kind}-load`}>Perbarui</Button>
      </CardContent></Card>
      {d && (
        <>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            <Stat label="CURRENT" value={formatRupiah(d.buckets?.CURRENT || 0)} tone="emerald" />
            <Stat label="1–30 hari" value={formatRupiah(d.buckets?.["1_30"] || 0)} />
            <Stat label="31–60 hari" value={formatRupiah(d.buckets?.["31_60"] || 0)} tone="amber" />
            <Stat label="61–90 hari" value={formatRupiah(d.buckets?.["61_90"] || 0)} tone="amber" />
            <Stat label=">90 hari" value={formatRupiah(d.buckets?.OVER_90 || 0)} tone="rose" />
          </div>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Badge className={d.status === "SESUAI" ? "bg-emerald-600" : "bg-rose-600"} data-testid={`aging-${kind}-status`}>{d.status}</Badge>
              <span className="text-sm text-slate-600">Total: {formatRupiah(d.total)} · GL: {formatRupiah(d.gl_ar_balance || d.gl_ap_balance)} · Selisih: {formatRupiah(d.diff_vs_gl)}</span>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>{kind === "ar" ? "Pelanggan" : "Pemasok"}</TableHead>
                <TableHead>CURRENT</TableHead><TableHead>1–30</TableHead><TableHead>31–60</TableHead>
                <TableHead>61–90</TableHead><TableHead>&gt;90</TableHead><TableHead>Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.party_code} data-testid={`aging-${kind}-row-${r.party_code}`}>
                    <TableCell>{r.party_name} <span className="text-xs text-slate-500">({r.party_code})</span></TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(r.buckets.CURRENT)}</TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(r.buckets["1_30"])}</TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(r.buckets["31_60"])}</TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(r.buckets["61_90"])}</TableCell>
                    <TableCell className="font-mono-num">{formatRupiah(r.buckets.OVER_90)}</TableCell>
                    <TableCell className="font-mono-num font-bold">{formatRupiah(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
export const ARAgingPage = () => <AgingPage kind="ar" />;
export const APAgingPage = () => <AgingPage kind="ap" />;

/* ============ Cash Position ============ */
export function CashPositionPage() {
  const can = useFinCan();
  const canView = can("FIN_CASH_POSITION_LIHAT");
  const [d, setD] = useState(null);
  useEffect(() => { if (!canView) return; api.get("/fin/cash-position").then(({data}) => setD(data)).catch(e => toast.error(formatApiError(e))); }, [canView]);
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Cash Position</h1><NoAccessBanner perm="FIN_CASH_POSITION_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="cash-position-page">
      <div><Crumb path={["Keuangan", "Cash Position"]} /><h1 className="font-display text-3xl font-bold">Cash Position & Dasbor Likuiditas</h1></div>
      {d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Bank" value={formatRupiah(d.total_bank)} testid="cp-bank" />
            <Stat label="Total Kas" value={formatRupiah(d.total_cash)} testid="cp-cash" />
            <Stat label="Kas Tersedia" value={formatRupiah(d.available_cash)} tone="emerald" testid="cp-available" />
            <Stat label="Nilai Persediaan" value={formatRupiah(d.inventory_value)} />
            <Stat label="AR Outstanding" value={formatRupiah(d.outstanding_ar)} />
            <Stat label="AR Terlambat" value={formatRupiah(d.overdue_ar)} tone="rose" testid="cp-overdue-ar" />
            <Stat label="AP Outstanding" value={formatRupiah(d.outstanding_ap)} />
            <Stat label="AP Terlambat" value={formatRupiah(d.overdue_ap)} tone="rose" testid="cp-overdue-ap" />
            <Stat label="Pajak Terutang" value={formatRupiah(d.tax_liabilities)} tone="amber" />
            <Stat label="Cash In 30d" value={formatRupiah(d.cash_inflow_30d)} tone="emerald" />
            <Stat label="Cash Out 30d" value={formatRupiah(d.cash_outflow_30d)} tone="rose" />
          </div>
          <Card><CardContent className="p-4">
            <h3 className="font-semibold mb-3">Proyeksi Arus Kas (dari due_date subledger AR/AP aktual)</h3>
            <Table>
              <TableHeader><TableRow><TableHead>Horizon</TableHead><TableHead>Kas Masuk (AR jatuh tempo)</TableHead><TableHead>Kas Keluar (AP jatuh tempo)</TableHead><TableHead>Proyeksi Saldo</TableHead></TableRow></TableHeader>
              <TableBody>
                {["7d", "14d", "30d"].map(h => (
                  <TableRow key={h} data-testid={`cp-forecast-${h}`}>
                    <TableCell className="font-semibold">{h === "7d" ? "7 Hari" : h === "14d" ? "14 Hari" : "30 Hari"}</TableCell>
                    <TableCell className="font-mono-num text-emerald-700">{formatRupiah(d.forecast?.[h]?.inflow)}</TableCell>
                    <TableCell className="font-mono-num text-rose-700">{formatRupiah(d.forecast?.[h]?.outflow)}</TableCell>
                    <TableCell className="font-mono-num font-bold">{formatRupiah(d.forecast?.[h]?.projected_balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="text-xs text-slate-500 mt-2">{d.note}</div>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

/* ============ Management Dashboard ============ */
export function ManagementDashboardPage() {
  const can = useFinCan();
  const canView = can("FIN_DASHBOARD_LIHAT");
  const [d, setD] = useState(null);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const load = async () => {
    try { const { data } = await api.get(`/fin/dashboard?year=${year}&month=${month}`); setD(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (canView) load(); }, [canView, year, month]);
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Dasbor Manajemen</h1><NoAccessBanner perm="FIN_DASHBOARD_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="mgmt-dashboard-page">
      <div><Crumb path={["Keuangan", "Dasbor Manajemen"]} /><h1 className="font-display text-3xl font-bold">Dasbor Manajemen Keuangan</h1></div>
      <Card><CardContent className="p-4 flex gap-3 items-end">
        <div><Label>Tahun</Label><Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || year)} data-testid="mgmt-year" /></div>
        <div><Label>Bulan</Label><Input type="number" min="1" max="12" value={month} onChange={e => setMonth(parseInt(e.target.value) || month)} data-testid="mgmt-month" /></div>
        <Button onClick={load} data-testid="mgmt-load">Perbarui</Button>
      </CardContent></Card>
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Pendapatan" value={formatRupiah(d.revenue)} tone="emerald" testid="mgmt-revenue" />
          <Stat label="HPP" value={formatRupiah(d.hpp)} />
          <Stat label="Laba Kotor" value={formatRupiah(d.gross_profit)} tone={d.gross_profit >= 0 ? "emerald" : "rose"} testid="mgmt-gross" />
          <Stat label="Margin Kotor" value={`${d.gross_margin_pct}%`} tone="emerald" />
          <Stat label="Beban Operasional" value={formatRupiah(d.opex)} />
          <Stat label="Laba Bersih" value={formatRupiah(d.net_profit)} tone={d.net_profit >= 0 ? "emerald" : "rose"} testid="mgmt-net" />
          <Stat label="Piutang (AR)" value={formatRupiah(d.ar)} />
          <Stat label="Hutang (AP)" value={formatRupiah(d.ap)} />
          <Stat label="Persediaan" value={formatRupiah(d.inventory)} />
          <Stat label="Kas & Bank" value={formatRupiah(d.cash_and_bank)} />
          <Stat label="Aset Tetap (Cost)" value={formatRupiah(d.fixed_asset_cost)} />
          <Stat label="Nilai Buku Aset" value={formatRupiah(d.fixed_asset_book_value)} />
          <Stat label="Pajak Terutang" value={formatRupiah(d.tax_liability)} tone="amber" />
          <Stat label="AR Terlambat" value={formatRupiah(d.overdue_ar)} tone="rose" />
          <Stat label="AP Terlambat" value={formatRupiah(d.overdue_ap)} tone="rose" />
        </div>
      )}
    </div>
  );
}

/* ============ Financial Integrity ============ */
export function FinancialIntegrityPage() {
  const can = useFinCan();
  const canView = can("FIN_INTEGRITY_LIHAT");
  const [d, setD] = useState(null);
  useEffect(() => { if (!canView) return; api.get("/fin/integrity").then(({data}) => setD(data)).catch(e => toast.error(formatApiError(e))); }, [canView]);
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Integritas Keuangan</h1><NoAccessBanner perm="FIN_INTEGRITY_LIHAT" /></div>;
  const badgeCls = (s) => s === "SESUAI" ? "bg-emerald-600" : "bg-rose-600";
  return (
    <div className="space-y-6 page-fade" data-testid="fin-integrity-page">
      <div><Crumb path={["Keuangan", "Integritas"]} />
        <div className="flex items-center gap-3">
          <h1 className="font-display text-3xl font-bold">Pusat Integritas Keuangan (12 Cek)</h1>
          {d && <Badge className={badgeCls(d.overall)} data-testid="fin-integrity-overall">{d.overall}</Badge>}
        </div>
      </div>
      {d && (
        <Card><CardContent className="p-4">
          <Table>
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Cek</TableHead><TableHead>Ekspektasi</TableHead><TableHead>Aktual</TableHead><TableHead>Selisih</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.checks.map((c, i) => (
                <TableRow key={i} data-testid={`fin-int-row-${i}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell className="font-mono-num">{formatRupiah(c.expected)}</TableCell>
                  <TableCell className="font-mono-num">{formatRupiah(c.actual)}</TableCell>
                  <TableCell className="font-mono-num">{formatRupiah(c.difference)}</TableCell>
                  <TableCell><Badge className={badgeCls(c.status)}>{c.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}

/* ============ Financial Close ============ */
export function FinancialClosePage() {
  const can = useFinCan();
  const canView = can("FIN_INTEGRITY_LIHAT");
  const canManage = can("FIN_CLOSE_KELOLA");
  const canApprove = can("FIN_CLOSE_APPROVE");
  const canLock = can("FIN_CLOSE_LOCK");
  const canReopen = can("FIN_CLOSE_REOPEN");
  const today = new Date();
  const [period, setPeriod] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [d, setD] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const load = async () => {
    try { const { data } = await api.get(`/fin/close/${period}`); setD(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (canView) load(); }, [canView, period]);
  const doAction = async (act, extraBody) => {
    try {
      await api.post(`/fin/close/${act}`, { period, ...(extraBody || {}) });
      toast.success(`${act.toUpperCase()} berhasil`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Tutup Buku</h1><NoAccessBanner perm="FIN_INTEGRITY_LIHAT" /></div>;
  const badgeCls = (s) => ({ OPEN: "bg-slate-500", PRE_CLOSE: "bg-amber-500", REVIEW: "bg-blue-500",
                             CLOSED: "bg-emerald-600", LOCKED: "bg-rose-700" })[s] || "bg-slate-500";
  return (
    <div className="space-y-6 page-fade" data-testid="fin-close-page">
      <div><Crumb path={["Keuangan", "Tutup Buku"]} /><h1 className="font-display text-3xl font-bold">Tutup Buku Periode (FSM 5-tahap + Reopen)</h1></div>
      <Card><CardContent className="p-4 flex flex-wrap gap-3 items-end">
        <div><Label>Periode (YYYY-MM)</Label><Input value={period} onChange={e => setPeriod(e.target.value)} placeholder="2027-01" data-testid="fc-period" /></div>
        <Button onClick={load} variant="outline" data-testid="fc-load">Muat</Button>
        {d && <Badge className={badgeCls(d.status)} data-testid="fc-status">{d.status}</Badge>}
      </CardContent></Card>
      {d?.checklist && (
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-3">Checklist Penutupan Periode</h3>
          <Table><TableBody>
            {Object.entries(d.checklist).filter(([k]) => k !== "period_month").map(([k, v]) => (
              <TableRow key={k}>
                <TableCell className="capitalize">{k.replace(/_/g, " ")}</TableCell>
                <TableCell><Badge className={v.status === "SESUAI" ? "bg-emerald-600" : "bg-rose-600"}>{v.status}</Badge></TableCell>
                <TableCell className="font-mono-num text-xs">{JSON.stringify(v).slice(0, 100)}</TableCell>
              </TableRow>
            ))}
          </TableBody></Table>
        </CardContent></Card>
      )}
      {d && (
        <Card><CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {d.status === "OPEN" && canManage && <Button onClick={() => doAction("pre-close")} data-testid="fc-preclose">Ajukan Pre-Close</Button>}
            {d.status === "PRE_CLOSE" && canManage && <Button onClick={() => doAction("review")} data-testid="fc-review">Kirim Review</Button>}
            {d.status === "REVIEW" && canApprove && <Button onClick={() => doAction("close")} data-testid="fc-close" className="bg-emerald-600">Tutup Periode</Button>}
            {d.status === "CLOSED" && canLock && <Button onClick={() => doAction("lock")} data-testid="fc-lock" className="bg-rose-700"><Lock className="h-4 w-4 mr-1" />Kunci Periode</Button>}
            {d.status === "LOCKED" && canReopen && (
              <div className="flex gap-2 items-end">
                <div className="flex-1"><Label>Alasan Reopen (min. 10 karakter)</Label><Input value={reopenReason} onChange={e => setReopenReason(e.target.value)} data-testid="fc-reopen-reason" /></div>
                <Button onClick={() => doAction("reopen", { reason: reopenReason })} data-testid="fc-reopen" variant="outline"><Unlock className="h-4 w-4 mr-1" />Reopen ke REVIEW</Button>
              </div>
            )}
          </div>
          {d.history && d.history.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">Riwayat Transisi</h4>
              <Table><TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Aksi</TableHead><TableHead>Oleh</TableHead><TableHead>Alasan</TableHead></TableRow></TableHeader>
                <TableBody>{d.history.map((h, i) => <TableRow key={i}><TableCell className="text-xs">{formatDateID(h.at)}</TableCell><TableCell>{h.action}</TableCell><TableCell>{h.by}</TableCell><TableCell className="text-xs">{h.reason || "-"}</TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

/* ============ Fixed Asset Register ============ */
export function FixedAssetRegisterPage() {
  const can = useFinCan();
  const canView = can("ASET_LIHAT");
  const canManage = can("ASET_KELOLA");
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dash, setDash] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", category_code: "MESIN",
                                       acquisition_date: new Date().toISOString().slice(0, 10),
                                       acquisition_cost: 0, residual_value: 0, useful_life_months: 60,
                                       depreciation_method: "Garis Lurus", supplier_code: "" });
  const load = async () => {
    try {
      const [a, c, dd] = await Promise.all([
        api.get("/asset/register"),
        api.get("/asset/categories"),
        api.get("/asset/dashboard").catch(() => ({ data: null })),
      ]);
      setAssets(a.data); setCategories(c.data); setDash(dd.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (canView) load(); }, [canView]);
  const submit = async () => {
    try {
      await api.post("/asset/register", { ...form,
        acquisition_cost: parseFloat(form.acquisition_cost),
        residual_value: parseFloat(form.residual_value),
        useful_life_months: parseInt(form.useful_life_months) });
      toast.success("Aset dibuat (DRAFT)");
      setShowForm(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const activate = async (id) => {
    try { await api.post(`/asset/register/${id}/activate`); toast.success("Aset di-activate & posting GL"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Register Aset</h1><NoAccessBanner perm="ASET_LIHAT" /></div>;
  const badgeCls = (s) => ({ DRAFT: "bg-slate-500", ACTIVE: "bg-emerald-600", AKTIF: "bg-emerald-600",
                              SOLD: "bg-blue-600", DISPOSED: "bg-amber-600", WRITTEN_OFF: "bg-rose-600" })[s] || "bg-slate-500";
  const assetByCat = (dash?.by_category || []).map((c) => ({
    name: c.category_code || "-", cost: Number(c.cost || 0), book_value: Number(c.book_value || 0),
  }));
  const depSeries = (dash?.depreciation_by_month || []).map((r) => ({
    name: r.period, dep: Number(r.amount || 0),
  }));
  return (
    <div className="space-y-6 page-fade" data-testid="asset-register-page">
      <div><Crumb path={["Aset Tetap", "Register"]} /><h1 className="font-display text-3xl font-bold">Register Aset Tetap</h1></div>
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ast-kpi">
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Jumlah Aset</div>
            <div className="text-xl font-bold text-slate-900">{dash.asset_count ?? dash.total_assets ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Nilai Perolehan</div>
            <div className="text-lg font-bold">{formatRupiah(dash.acquisition_value ?? dash.total_cost)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Akumulasi Penyusutan</div>
            <div className="text-lg font-bold">{formatRupiah(dash.accumulated_depreciation ?? dash.total_accumulated_depreciation)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Nilai Buku Bersih</div>
            <div className="text-lg font-bold">{formatRupiah(dash.net_book_value ?? dash.total_book_value)}</div>
            <div className="text-xs text-slate-500 mt-1">{dash.assets_near_end_of_life || 0} aset mendekati akhir umur manfaat</div>
          </CardContent></Card>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HorizontalBarCard
          title="Nilai Aset per Kategori"
          testId="chart-asset-category"
          hint="Belum ada aset aktif per kategori."
          currency
          data={assetByCat}
          bars={[
            { key: "cost", label: "Nilai Perolehan", color: CHART_COLORS.baseline },
            { key: "book_value", label: "Nilai Buku", color: CHART_COLORS.positive },
          ]}
          height={Math.max(220, assetByCat.length * 48 + 60)}
        />
        <LineCard
          title="Penyusutan per Periode"
          testId="chart-asset-depreciation"
          hint="Belum ada posting penyusutan pada aset."
          currency
          data={depSeries}
          lines={[{ key: "dep", label: "Beban Penyusutan", color: CHART_COLORS.warning }]}
        />
      </div>
      {canManage && <Button onClick={() => setShowForm(!showForm)} data-testid="ast-toggle-form">{showForm ? "Batal" : "+ Tambah Aset"}</Button>}
      {showForm && canManage && (
        <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Kode</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} data-testid="ast-code" /></div>
          <div><Label>Nama</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ast-name" /></div>
          <div><Label>Kategori</Label><Select value={form.category_code} onValueChange={v => setForm({...form, category_code: v})}><SelectTrigger data-testid="ast-cat"><SelectValue /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Tanggal Akuisisi</Label><Input type="date" value={form.acquisition_date} onChange={e => setForm({...form, acquisition_date: e.target.value})} data-testid="ast-date" /></div>
          <div><Label>Harga Perolehan</Label><Input type="number" value={form.acquisition_cost} onChange={e => setForm({...form, acquisition_cost: e.target.value})} data-testid="ast-cost" /></div>
          <div><Label>Nilai Residu</Label><Input type="number" value={form.residual_value} onChange={e => setForm({...form, residual_value: e.target.value})} data-testid="ast-residual" /></div>
          <div><Label>Masa Manfaat (bulan)</Label><Input type="number" value={form.useful_life_months} onChange={e => setForm({...form, useful_life_months: e.target.value})} data-testid="ast-life" /></div>
          <div className="col-span-2 md:col-span-3"><Button onClick={submit} data-testid="ast-submit">Simpan (DRAFT)</Button></div>
        </CardContent></Card>
      )}
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Kategori</TableHead><TableHead>Cost</TableHead><TableHead>Akum Dep</TableHead><TableHead>Nilai Buku</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {assets.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">TIDAK ADA DATA</TableCell></TableRow>}
            {assets.map(a => (
              <TableRow key={a.id} data-testid={`ast-row-${a.code}`}>
                <TableCell className="font-mono-num">{a.code}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell>{a.category_code}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(a.acquisition_cost)}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(a.accumulated_depreciation)}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah(a.current_book_value)}</TableCell>
                <TableCell><Badge className={badgeCls(a.status)}>{a.status}</Badge></TableCell>
                <TableCell>{canManage && a.status === "DRAFT" && <Button size="sm" onClick={() => activate(a.id)} data-testid={`ast-activate-${a.code}`}>Activate</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

/* ============ Depreciation Batch ============ */
export function DepreciationBatchPage() {
  const can = useFinCan();
  const canView = can("ASET_LIHAT");
  const canPost = can("ASET_DEPRECIATION") || can("*");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [result, setResult] = useState(null);
  const post = async () => {
    try { const { data } = await api.post("/asset/depreciation/batch", { period }); setResult(data); toast.success(`Batch selesai — ${data.posted_count} posted, ${data.skipped_count} skipped`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Penyusutan Batch</h1><NoAccessBanner perm="ASET_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="depreciation-batch-page">
      <div><Crumb path={["Aset Tetap", "Penyusutan Batch"]} /><h1 className="font-display text-3xl font-bold">Penyusutan Batch (Idempotent per Periode)</h1></div>
      <Card><CardContent className="p-4 flex gap-3 items-end">
        <div><Label>Periode (YYYY-MM)</Label><Input value={period} onChange={e => setPeriod(e.target.value)} data-testid="dep-period" /></div>
        {canPost && <Button onClick={post} data-testid="dep-post">Posting Penyusutan Batch</Button>}
      </CardContent></Card>
      {result && (
        <Card><CardContent className="p-4 space-y-3">
          <div>Periode <strong>{result.period}</strong>: {result.posted_count} posted, {result.skipped_count} dilewati.</div>
          {result.posted.length > 0 && <div className="text-sm">✓ Posted: {result.posted.map(p => `${p.code} (${formatRupiah(p.amount)})`).join(", ")}</div>}
          {result.skipped.length > 0 && <div className="text-sm text-amber-700">Skipped: {result.skipped.map(s => `${s.code} (${s.reason})`).join(", ")}</div>}
        </CardContent></Card>
      )}
    </div>
  );
}

/* ============ Asset Disposal ============ */
export function AssetDisposalPage() {
  const can = useFinCan();
  const canView = can("ASET_LIHAT");
  const canDispose = can("ASET_DISPOSAL_CREATE") || can("ASET_DISPOSAL_APPROVE") || can("*");
  const [assets, setAssets] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({ asset_id: "", disposal_type: "SALE", disposal_date: new Date().toISOString().slice(0, 10),
                                       proceeds: 0, bank_or_cash_code: "", bank_or_cash_type: "BANK", reason: "" });
  useEffect(() => { if (!canView) return; Promise.all([api.get("/asset/register?status=ACTIVE"), api.get("/bank/accounts")]).then(([a, b]) => { setAssets(a.data); setBanks(b.data); }).catch(e => toast.error(formatApiError(e))); }, [canView]);
  const submit = async () => {
    try {
      await api.post("/asset/disposal", { ...form, proceeds: parseFloat(form.proceeds) });
      toast.success("Disposal berhasil di-post");
      setForm({ ...form, asset_id: "", proceeds: 0, reason: "" });
      const { data } = await api.get("/asset/register?status=ACTIVE"); setAssets(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Pelepasan Aset</h1><NoAccessBanner perm="ASET_LIHAT" /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="asset-disposal-page">
      <div><Crumb path={["Aset Tetap", "Pelepasan"]} /><h1 className="font-display text-3xl font-bold">Pelepasan / Penjualan / Penghapusan Aset</h1></div>
      {canDispose && (
        <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Aset (Active)</Label><Select value={form.asset_id} onValueChange={v => setForm({...form, asset_id: v})}><SelectTrigger data-testid="dsp-asset"><SelectValue placeholder="Pilih aset" /></SelectTrigger><SelectContent>{assets.map(a => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name} (BV {formatRupiah(a.current_book_value)})</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Tipe Pelepasan</Label><Select value={form.disposal_type} onValueChange={v => setForm({...form, disposal_type: v})}><SelectTrigger data-testid="dsp-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="SALE">Penjualan</SelectItem><SelectItem value="DISPOSAL">Pelepasan (Non-Sale)</SelectItem><SelectItem value="WRITE_OFF">Write-Off</SelectItem></SelectContent></Select></div>
          <div><Label>Tanggal</Label><Input type="date" value={form.disposal_date} onChange={e => setForm({...form, disposal_date: e.target.value})} data-testid="dsp-date" /></div>
          {form.disposal_type === "SALE" && <>
            <div><Label>Proceeds</Label><Input type="number" value={form.proceeds} onChange={e => setForm({...form, proceeds: e.target.value})} data-testid="dsp-proceeds" /></div>
            <div><Label>Bank Penerima</Label><Select value={form.bank_or_cash_code} onValueChange={v => setForm({...form, bank_or_cash_code: v})}><SelectTrigger data-testid="dsp-bank"><SelectValue placeholder="Pilih bank" /></SelectTrigger><SelectContent>{banks.map(b => <SelectItem key={b.code} value={b.code}>{b.code} — {b.bank_name}</SelectItem>)}</SelectContent></Select></div>
          </>}
          <div className="col-span-2 md:col-span-3"><Label>Alasan (wajib untuk audit)</Label><Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} data-testid="dsp-reason" /></div>
          <div className="col-span-2 md:col-span-3"><Button onClick={submit} data-testid="dsp-submit" className="bg-rose-700">Post Pelepasan</Button></div>
        </CardContent></Card>
      )}
    </div>
  );
}

/* ============ Bank Auto-Rule Matcher ============ */
export function BankAutoRulesPage() {
  const can = useFinCan();
  const { user } = useAuth();
  const canView = can("*") || ["akuntansi", "keuangan", "direktur_utama", "auditor", "super_admin"].includes(user?.role);
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description_keywords: "", reference_keywords: "",
                                       amount_min: "", amount_max: "", amount_exact: "",
                                       direction: "BOTH", priority: 100, active: true });
  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get("/bank/auto-rules"), api.get("/bank/auto-rules/suggest?limit=50")]);
      setRules(r.data); setSuggestions(s.data.suggestions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (canView) load(); }, [canView]);
  const submit = async () => {
    try {
      const body = {
        name: form.name,
        description_keywords: form.description_keywords ? form.description_keywords.split(",").map(s => s.trim()).filter(Boolean) : null,
        reference_keywords: form.reference_keywords ? form.reference_keywords.split(",").map(s => s.trim()).filter(Boolean) : null,
        amount_min: form.amount_min ? parseFloat(form.amount_min) : null,
        amount_max: form.amount_max ? parseFloat(form.amount_max) : null,
        amount_exact: form.amount_exact ? parseFloat(form.amount_exact) : null,
        direction: form.direction, priority: parseInt(form.priority), active: form.active,
      };
      await api.post("/bank/auto-rules", body);
      toast.success("Aturan dibuat");
      setShowForm(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus aturan ini?")) return;
    try { await api.delete(`/bank/auto-rules/${id}`); toast.success("Dihapus"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!canView) return <div className="space-y-6 page-fade"><h1 className="font-display text-3xl font-bold">Aturan Auto-Match</h1><NoAccessBanner /></div>;
  return (
    <div className="space-y-6 page-fade" data-testid="bank-auto-rules-page">
      <div><Crumb path={["Bank", "Aturan Auto-Match"]} /><h1 className="font-display text-3xl font-bold">Bank Auto-Rule Matcher (Suggestion-Only)</h1>
        <p className="text-sm text-slate-500 mt-2">Aturan menghasilkan <strong>suggestion + confidence</strong>. TIDAK auto-confirm/auto-post — user wajib approve di halaman Matching.</p>
      </div>
      <Button onClick={() => setShowForm(!showForm)} data-testid="ar-toggle">{showForm ? "Batal" : "+ Aturan Baru"}</Button>
      {showForm && (
        <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Nama Aturan</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ar-name" /></div>
          <div><Label>Prioritas (kecil = tinggi)</Label><Input type="number" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} data-testid="ar-priority" /></div>
          <div><Label>Arah</Label><Select value={form.direction} onValueChange={v => setForm({...form, direction: v})}><SelectTrigger data-testid="ar-direction"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="INCOMING">Kredit (Masuk)</SelectItem><SelectItem value="OUTGOING">Debit (Keluar)</SelectItem><SelectItem value="BOTH">Keduanya</SelectItem></SelectContent></Select></div>
          <div className="col-span-2 md:col-span-3"><Label>Keyword Deskripsi (pisah dengan koma)</Label><Input value={form.description_keywords} onChange={e => setForm({...form, description_keywords: e.target.value})} placeholder="mis: PLN, Listrik, Air, Bumn" data-testid="ar-desc-kw" /></div>
          <div className="col-span-2 md:col-span-3"><Label>Keyword Referensi (pisah dengan koma)</Label><Input value={form.reference_keywords} onChange={e => setForm({...form, reference_keywords: e.target.value})} data-testid="ar-ref-kw" /></div>
          <div><Label>Nominal Min</Label><Input type="number" value={form.amount_min} onChange={e => setForm({...form, amount_min: e.target.value})} data-testid="ar-amt-min" /></div>
          <div><Label>Nominal Max</Label><Input type="number" value={form.amount_max} onChange={e => setForm({...form, amount_max: e.target.value})} data-testid="ar-amt-max" /></div>
          <div><Label>Nominal Exact</Label><Input type="number" value={form.amount_exact} onChange={e => setForm({...form, amount_exact: e.target.value})} data-testid="ar-amt-exact" /></div>
          <div className="col-span-2 md:col-span-3"><Button onClick={submit} data-testid="ar-submit">Simpan Aturan</Button></div>
        </CardContent></Card>
      )}
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Arah</TableHead><TableHead>Keywords Desc</TableHead><TableHead>Nominal</TableHead><TableHead>Prioritas</TableHead><TableHead>Aktif</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {rules.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">Belum ada aturan</TableCell></TableRow>}
            {rules.map(r => (
              <TableRow key={r.id} data-testid={`ar-row-${r.id}`}>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.direction}</TableCell>
                <TableCell className="text-xs">{(r.description_keywords || []).join(", ")}</TableCell>
                <TableCell className="text-xs">{r.amount_exact ? `= ${formatRupiah(r.amount_exact)}` : (r.amount_min || r.amount_max ? `${r.amount_min || 0}-${r.amount_max || "∞"}` : "-")}</TableCell>
                <TableCell>{r.priority}</TableCell>
                <TableCell><Badge className={r.active ? "bg-emerald-600" : "bg-slate-400"}>{r.active ? "AKTIF" : "NONAKTIF"}</Badge></TableCell>
                <TableCell><Button size="sm" variant="destructive" onClick={() => del(r.id)} data-testid={`ar-del-${r.id}`}>Hapus</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      {suggestions.length > 0 && (
        <Card><CardContent className="p-4">
          <h3 className="font-semibold mb-3">Suggestion Aktif ({suggestions.length}) — <span className="text-rose-600">Wajib Approve Manual di halaman Matching</span></h3>
          <Table>
            <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Deskripsi</TableHead><TableHead>Nominal</TableHead><TableHead>Aturan</TableHead><TableHead>Confidence</TableHead></TableRow></TableHeader>
            <TableBody>{suggestions.map((s, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs">{s.date}</TableCell>
                <TableCell className="text-xs">{s.description}</TableCell>
                <TableCell className="font-mono-num">{formatRupiah((s.credit || 0) || (s.debit || 0))}</TableCell>
                <TableCell>{s.suggestion?.rule_name}</TableCell>
                <TableCell><Badge className={s.suggestion?.confidence === "TINGGI" ? "bg-emerald-600" : s.suggestion?.confidence === "SEDANG" ? "bg-amber-500" : "bg-slate-500"}>{s.suggestion?.confidence}</Badge></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}
